import React, { useState } from "react";
import { Box, Typography, Chip, Collapse, Stack, Link, alpha, useTheme, Tooltip } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ApiEndpoint } from "../../types";
import {
  getIntelligentFields,
  getHighPriorityFields,
  getCategoryInfo,
  FieldInfo,
  CategorizedFields
} from "../../utilities/intelligentFieldSelection";

/** Compact section headers (full mode): icon only, full name in tooltip */
const CATEGORY_HEADER_ICONS: Record<keyof CategorizedFields, typeof ArticleOutlinedIcon> = {
  details: ArticleOutlinedIcon,
  temporal: ScheduleOutlinedIcon,
  technical: StraightenOutlinedIcon,
  metadata: InfoOutlinedIcon
};

// Field display configuration constants

interface IntelligentFieldDisplayProps {
  item: any;
  endpoint?: ApiEndpoint;
  mode?: "compact" | "full";
  maxCompactFields?: number;
  /** Light “card” table (default) vs minimal rows on dark popup chrome */
  surface?: "panel" | "embeddedDark";
}

/**
 * Format field value for display based on its data type
 */
const formatFieldValue = (field: FieldInfo, embeddedDark = false): React.ReactNode => {
  const { value, dataType } = field;
  const ed = embeddedDark;

  if (value === null || value === undefined) {
    return (
      <Typography
        component='span'
        color={ed ? undefined : "text.secondary"}
        fontStyle='italic'
        sx={ed ? { color: "rgba(252, 252, 253, 0.45)" } : undefined}
      >
        No value
      </Typography>
    );
  }

  switch (dataType) {
    case "boolean":
      return (
        <Chip
          label={value ? "Yes" : "No"}
          size='small'
          color={ed ? undefined : value ? "success" : "default"}
          variant={ed ? "outlined" : "filled"}
          sx={{
            fontWeight: 500,
            fontSize: "0.8rem",
            height: 28,
            "& .MuiChip-label": {
              px: 1.5
            },
            ...(ed && {
              color: "#F2F4F7",
              borderColor: "rgba(255, 255, 255, 0.28)",
              backgroundColor: value ? "rgba(16, 185, 129, 0.2)" : "rgba(255, 255, 255, 0.06)"
            })
          }}
        />
      );

    case "date":
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return (
            <Typography
              component='span'
              sx={{
                fontFamily: "monospace",
                fontSize: "0.9rem",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                ...(ed
                  ? {
                      color: "#F2F4F7",
                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                      border: "1px solid rgba(255, 255, 255, 0.12)"
                    }
                  : {
                      border: `1px solid ${alpha("#000", 0.08)}`
                    })
              }}
            >
              {date.toLocaleDateString()}{" "}
              {date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit"
              })}
            </Typography>
          );
        }
      } catch {
        // Fall through to default
      }
      return (
        <Typography component='span' sx={ed ? { color: "#F2F4F7" } : undefined}>
          {String(value)}
        </Typography>
      );

    case "url":
      return (
        <Link
          href={value}
          target='_blank'
          rel='noopener noreferrer'
          sx={
            ed
              ? {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  wordBreak: "break-all",
                  color: "#F2F4F7",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  textDecoration: "none",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.35)",
                  "&:hover": {
                    color: "#FFFFFF",
                    borderBottomColor: "rgba(255, 255, 255, 0.65)"
                  }
                }
              : (theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  wordBreak: "break-all",
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  textDecoration: "none",
                  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    borderBottomColor: theme.palette.primary.main
                  }
                })
          }
        >
          {String(value)}
          <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.65 }} />
        </Link>
      );

    case "number":
      return (
        <Typography
          component='span'
          sx={{
            fontFamily: 'ui-monospace, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 1.125,
            py: 0.35,
            borderRadius: 1,
            ...(ed
              ? {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  color: "#F2F4F7",
                  border: "1px solid rgba(255, 255, 255, 0.14)"
                }
              : {
                  backgroundColor: alpha("#0f172a", 0.045),
                  color: alpha("#0f172a", 0.82),
                  border: `1px solid ${alpha("#0f172a", 0.08)}`
                })
          }}
        >
          {typeof value === "number" ? value.toLocaleString() : String(value)}
        </Typography>
      );

    case "array":
      if (Array.isArray(value) && value.length > 0) {
        return (
          <Box component='span' sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {value.map((item, index) => (
              <Chip
                key={index}
                label={String(item)}
                size='small'
                variant={ed ? "outlined" : "filled"}
                sx={{
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  height: 28,
                  ...(ed
                    ? {
                        color: "#F2F4F7",
                        borderColor: "rgba(255, 255, 255, 0.22)",
                        backgroundColor: "rgba(255, 255, 255, 0.06)",
                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" }
                      }
                    : {
                        backgroundColor: alpha("#f0f4f8", 0.8),
                        color: "text.primary",
                        border: `1px solid ${alpha("#000", 0.08)}`,
                        "&:hover": {
                          backgroundColor: alpha("#e2e8f0", 0.9)
                        }
                      })
                }}
              />
            ))}
          </Box>
        );
      }
      return (
        <Typography
          component='span'
          color={ed ? undefined : "text.secondary"}
          fontStyle='italic'
          sx={ed ? { color: "rgba(252, 252, 253, 0.45)" } : undefined}
        >
          Empty list
        </Typography>
      );

    case "object":
      if (typeof value === "object" && value !== null) {
        // Handle Fulcrum record link fields specially
        if ((value as any).record_id) {
          return (
            <Chip
              label='Linked Record'
              size='small'
              variant={ed ? "outlined" : "filled"}
              color={ed ? undefined : "info"}
              sx={{
                fontWeight: 500,
                fontSize: "0.8rem",
                height: 28,
                "& .MuiChip-label": {
                  px: 1.5
                },
                ...(ed && {
                  color: "#E0E7FF",
                  borderColor: "rgba(129, 140, 248, 0.55)",
                  backgroundColor: "rgba(99, 102, 241, 0.15)"
                })
              }}
            />
          );
        }

        // Handle Fulcrum photo fields specially
        if ((value as any).photo_id) {
          return (
            <Chip
              label='Photo Attachment'
              size='small'
              variant={ed ? "outlined" : "filled"}
              color={ed ? undefined : "secondary"}
              sx={{
                fontWeight: 500,
                fontSize: "0.8rem",
                height: 28,
                "& .MuiChip-label": {
                  px: 1.5
                },
                ...(ed && {
                  color: "#FCE7F3",
                  borderColor: "rgba(244, 114, 182, 0.45)",
                  backgroundColor: "rgba(244, 114, 182, 0.12)"
                })
              }}
            />
          );
        }

        // Handle Fulcrum choice fields specially
        if ((value as any).choice_values || (value as any).other_values) {
          const choices = [...((value as any).choice_values || []), ...((value as any).other_values || [])];
          if (choices.length > 0) {
            return (
              <Box component='span' sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {choices.map((choice: any, index: number) => (
                  <Chip
                    key={index}
                    label={String(choice)}
                    size='small'
                    variant={ed ? "outlined" : "filled"}
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.8rem",
                      height: 28,
                      ...(ed
                        ? {
                            color: "#F2F4F7",
                            borderColor: "rgba(255, 255, 255, 0.22)",
                            backgroundColor: "rgba(255, 255, 255, 0.06)",
                            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" }
                          }
                        : {
                            backgroundColor: alpha("#f0f4f8", 0.8),
                            color: "text.primary",
                            border: `1px solid ${alpha("#000", 0.08)}`,
                            "&:hover": {
                              backgroundColor: alpha("#e2e8f0", 0.9)
                            }
                          })
                    }}
                  />
                ))}
              </Box>
            );
          }
        }

        // For other objects, show a clean representation
        const keys = Object.keys(value);
        if (keys.length === 0) {
          return (
            <Typography
              component='span'
              color={ed ? undefined : "text.secondary"}
              fontStyle='italic'
              sx={ed ? { color: "rgba(252, 252, 253, 0.45)" } : undefined}
            >
              Empty object
            </Typography>
          );
        }

        return (
          <Chip
            label={`Object (${keys.length} ${keys.length === 1 ? "property" : "properties"})`}
            size='small'
            variant='outlined'
            sx={{
              fontWeight: 500,
              fontSize: "0.8rem",
              height: 28,
              ...(ed && {
                color: "rgba(252, 252, 253, 0.85)",
                borderColor: "rgba(255, 255, 255, 0.28)"
              })
            }}
          />
        );
      }
      return (
        <Typography
          component='span'
          color={ed ? undefined : "text.secondary"}
          fontStyle='italic'
          sx={ed ? { color: "rgba(252, 252, 253, 0.45)" } : undefined}
        >
          No data
        </Typography>
      );

    default:
      const stringValue = String(value);
      return (
        <Typography component='span' sx={ed ? { color: "#F2F4F7" } : undefined}>
          {stringValue}
        </Typography>
      );
  }
};

/**
 * Render a single field row
 */
const FieldRow: React.FC<{ field: FieldInfo; embeddedDark?: boolean }> = ({ field, embeddedDark = false }) => {
  const theme = useTheme();
  const ed = embeddedDark;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { sm: "flex-start" },
        gap: { xs: 0.35, sm: 2 },
        py: ed ? 0.625 : 0.75,
        px: ed ? { xs: 0.5, sm: 1 } : 1.25,
        transition: "background-color 0.15s ease",
        ...(ed
          ? {
              borderBottom: "1px solid rgba(255, 255, 255, 0.07)",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.04)" },
              "&:last-child": { borderBottom: "none" }
            }
          : {
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.055)}`,
              "&:hover": {
                backgroundColor: alpha(theme.palette.action.hover, 0.035)
              },
              "&:last-child": {
                borderBottom: "none"
              }
            })
      }}
    >
      <Box
        sx={{
          flex: { xs: "none", sm: ed ? "0 1 31%" : "0 1 34%" },
          width: { xs: "100%", sm: "auto" },
          maxWidth: { sm: ed ? "10.5rem" : "12.25rem" },
          minWidth: 0,
          pr: { sm: 1 },
          mb: { xs: 0.25, sm: 0 }
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontWeight: 500,
            fontSize: "0.75rem",
            letterSpacing: "0.02em",
            lineHeight: 1.45,
            color: ed ? "rgba(252, 252, 253, 0.55)" : theme.palette.text.secondary
          }}
        >
          {field.displayName}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: { xs: "none", sm: ed ? "1 1 69%" : "1 1 66%" },
          width: { xs: "100%", sm: "auto" },
          minWidth: 0,
          display: "flex",
          alignItems: "center"
        }}
      >
        <Typography
          variant='body2'
          component='div'
          sx={{
            fontSize: "0.875rem",
            lineHeight: 1.55,
            wordBreak: "break-word",
            fontWeight: 400,
            color: ed ? "#F2F4F7" : theme.palette.text.primary
          }}
        >
          {formatFieldValue(field, ed)}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Render a category section with accordion
 */
const CategorySection: React.FC<{
  category: keyof CategorizedFields;
  fields: FieldInfo[];
  defaultExpanded?: boolean;
  embeddedDark?: boolean;
}> = ({ category, fields, defaultExpanded = false, embeddedDark = false }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const categoryInfo = getCategoryInfo(category);
  const HeaderIcon = CATEGORY_HEADER_ICONS[category];
  const ed = embeddedDark;

  if (fields.length === 0) return null;

  return (
    <Box
      sx={
        ed
          ? {
              mb: 0.5,
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backgroundColor: "transparent",
              boxShadow: "none"
            }
          : {
              mb: 1,
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${alpha(theme.palette.divider, 0.09)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.5),
              boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`
            }
      }
    >
      <Tooltip title={categoryInfo.label}>
        <Box
          onClick={() => setExpanded(!expanded)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded(!expanded);
            }
          }}
          role='button'
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={categoryInfo.label}
          sx={
            ed
              ? {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: expanded ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(255, 255, 255, 0.06)",
                  backgroundColor: expanded ? "rgba(255, 255, 255, 0.06)" : "rgba(255, 255, 255, 0.03)",
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
                }
              : {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.625,
                  cursor: "pointer",
                  borderBottom: `1px solid ${alpha(theme.palette.divider, expanded ? 0.08 : 0.04)}`,
                  backgroundColor: expanded ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.action.hover, 0.02),
                  transition: "background-color 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.07)
                  }
                }
          }
        >
          <HeaderIcon
            sx={
              ed
                ? {
                    fontSize: "1.2rem",
                    color: expanded ? "rgba(199, 210, 254, 0.95)" : "rgba(252, 252, 253, 0.78)"
                  }
                : {
                    fontSize: "1.25rem",
                    color: expanded ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.88)
                  }
            }
          />
          <ExpandMoreIcon
            fontSize='small'
            sx={{
              transition: "transform 0.25s ease",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              color: ed
                ? expanded
                  ? "rgba(199, 210, 254, 0.95)"
                  : "rgba(252, 252, 253, 0.6)"
                : expanded
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.secondary, 0.75)
            }}
          />
        </Box>
      </Tooltip>

      <Collapse in={expanded}>
        <Box sx={{ py: 0 }}>
          <Stack spacing={0}>
            {fields.map((field) => (
              <FieldRow key={field.key} field={field} embeddedDark={ed} />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Main intelligent field display component
 */
const IntelligentFieldDisplay: React.FC<IntelligentFieldDisplayProps> = ({
  item,
  endpoint,
  mode = "full",
  maxCompactFields = 6,
  surface = "panel"
}) => {
  const [showAll, setShowAll] = useState(false);

  if (mode === "compact") {
    const highPriorityFields = getHighPriorityFields(item, endpoint);

    if (highPriorityFields.length === 0) {
      return null;
    }

    const displayFields = showAll ? highPriorityFields : highPriorityFields.slice(0, maxCompactFields);

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant='subtitle2' sx={{ mb: 1, color: "text.secondary" }}>
          Key Details
        </Typography>
        <Stack spacing={0}>
          {displayFields.map((field) => (
            <FieldRow key={field.key} field={field} />
          ))}
        </Stack>
        {highPriorityFields.length > maxCompactFields && (
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography
              variant='caption'
              sx={{
                color: "primary.main",
                cursor: "pointer",
                textDecoration: "underline"
              }}
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "Show Less" : `Show ${highPriorityFields.length - maxCompactFields} More`}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Full mode - show categorized fields
  const categorizedFields = getIntelligentFields(item, endpoint);

  // Check if we have any fields to display
  const totalFields = Object.values(categorizedFields).reduce((sum, fields) => sum + fields.length, 0);

  if (totalFields === 0) {
    return null;
  }

  const embeddedDark = surface === "embeddedDark";

  // Map popup: one continuous table (details → temporal → technical → metadata)
  if (embeddedDark) {
    const flatFields = [
      ...categorizedFields.details,
      ...categorizedFields.temporal,
      ...categorizedFields.technical,
      ...categorizedFields.metadata
    ];
    return (
      <Box sx={{ px: 0, width: "100%", alignSelf: "stretch" }}>
        <Stack spacing={0}>
          {flatFields.map((field, idx) => (
            <FieldRow key={`${String(field.key)}-${idx}`} field={field} embeddedDark />
          ))}
        </Stack>
      </Box>
    );
  }

  const smartExpansion = getSmartExpansionState(item, categorizedFields);

  return (
    <Box sx={{ px: 0.25 }}>
      <Stack spacing={0.75}>
        <CategorySection category='details' fields={categorizedFields.details} defaultExpanded={true} />
        <CategorySection
          category='temporal'
          fields={categorizedFields.temporal}
          defaultExpanded={smartExpansion.temporal}
        />
        <CategorySection
          category='technical'
          fields={categorizedFields.technical}
          defaultExpanded={smartExpansion.technical}
        />
        <CategorySection category='metadata' fields={categorizedFields.metadata} defaultExpanded={smartExpansion.metadata} />
      </Stack>
    </Box>
  );
};

// Smart expansion configuration constants
const SMART_EXPANSION_CONFIG = {
  /** Minimum character length for a meaningful description */
  MIN_MEANINGFUL_DESCRIPTION_LENGTH: 10,
  /** Maximum descriptive fields before considering content "sparse" */
  MAX_SPARSE_DESCRIPTIVE_FIELDS: 2,
  /** Maximum temporal fields before expanding technical section */
  MAX_SPARSE_TEMPORAL_FIELDS: 1
} as const;

/**
 * Smart expansion logic: determine which sections should be expanded by default
 * This provides an intelligent, adaptive UX that works across different datasets and customers
 */
const getSmartExpansionState = (item: any, categorizedFields: CategorizedFields): Record<string, boolean> => {
  // Details section is always expanded, so we only need to determine other sections
  const expansionState = {
    details: true, // Always expanded
    temporal: false,
    technical: false,
    metadata: false
  };

  // Check multiple sources for meaningful description content
  const hasMainDescription =
    item?.description &&
    typeof item.description === "string" &&
    item.description.trim().length > SMART_EXPANSION_CONFIG.MIN_MEANINGFUL_DESCRIPTION_LENGTH;

  // Also check Fulcrum form values for description-like fields
  const hasFulcrumDescription =
    item?.fulcrum_data?.form_values &&
    Object.entries(item.fulcrum_data.form_values).some(([key, value]) => {
      const isDescField =
        key.toLowerCase().includes("description") ||
        key.toLowerCase().includes("notes") ||
        key.toLowerCase().includes("comment") ||
        key.toLowerCase().includes("observation");
      return isDescField && typeof value === "string" && value.trim().length > SMART_EXPANSION_CONFIG.MIN_MEANINGFUL_DESCRIPTION_LENGTH;
    });

  const hasAnyDescription = hasMainDescription || hasFulcrumDescription;

  // Smart expansion based on content availability
  if (!hasAnyDescription) {
    // No meaningful description found - expand sections to provide context

    // If details section is sparse, also expand temporal for additional context
    if (categorizedFields.details.length <= SMART_EXPANSION_CONFIG.MAX_SPARSE_DESCRIPTIVE_FIELDS && categorizedFields.temporal.length > 0) {
      expansionState.temporal = true;
    }

    // For very sparse items, also show technical details
    if (
      categorizedFields.details.length === 0 &&
      categorizedFields.temporal.length <= SMART_EXPANSION_CONFIG.MAX_SPARSE_TEMPORAL_FIELDS &&
      categorizedFields.technical.length > 0
    ) {
      expansionState.technical = true;
    }
  }

  return expansionState;
};

export default IntelligentFieldDisplay;
